import { DestroyRef, inject, Injectable } from '@angular/core';
import { SbLineChartModel } from '@ngx-seatback/charts';
import { API, FrameBucketDTO, LocalizedFrameBucketDTO, NamedIdentityDTO } from './api/api';
import { SbLocalDateTime } from './api/api-date-time';
import { DateTime } from 'luxon';
import { switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PermissionService } from './permission.service';
import { Observable, of } from 'rxjs';

type DataArrayType =
  | 'CampaignWithPreviewDTO'
  | 'CampaignDTO'
  | 'CommercialWithPreviewDTO'
  | 'CommercialDTO'
  | 'RequirementDTO'
  | 'BookingDTO';

@Injectable()
export class CampaignReportingObsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(API);
  private readonly permissionService = inject(PermissionService);

  from: SbLocalDateTime = new SbLocalDateTime(DateTime.utc().startOf('day').minus({ day: 8 }));
  till: SbLocalDateTime = new SbLocalDateTime(DateTime.utc().endOf('day').minus({ day: 1 }));

  private filterData(data: FrameBucketDTO[] | LocalizedFrameBucketDTO[], searchVal: number) {
    return data[data.findIndex(x => x.key.id === searchVal)].frames;
  }

  public getAndMergePulseChart<T extends NamedIdentityDTO>(
    elementArray: T[],
    type: DataArrayType
  ): Observable<(T & { reporting?: SbLineChartModel })[]> {
    if (elementArray?.length <= 0) {
      return of([] as T[]);
    }
    if (!this.permissionService.hasPermission(this.permissionService.Permission.ds_cm_reporting)) {
      return of(elementArray);
    }
    const elementIds = elementArray.map(campaign => campaign.id);
    switch (type) {
      case 'CampaignWithPreviewDTO':
      case 'CampaignDTO':
        return this.retrieveCampaignReporting<T>(elementIds, elementArray);
      case 'CommercialWithPreviewDTO':
      case 'CommercialDTO':
        return this.retrieveCommercialReporting<T>(elementIds, elementArray);
      case 'BookingDTO':
        return this.retrieveBookingsReporting<T>(elementIds, elementArray);
      case 'RequirementDTO':
        return this.retrieveRequirementsReporting<T>(elementIds, elementArray);
    }
  }

  private buildPulseChartData(data: FrameBucketDTO[] | LocalizedFrameBucketDTO[], element: any): SbLineChartModel {
    if (!data || data.length === 0 || !element) {
      return null;
    }
    const playerTimeChartData = [];
    let chartData: SbLineChartModel;
    const tempData = this.filterData(data, element.id);
    if (!tempData || tempData.length === 0) {
      return null;
    }
    for (const i of tempData) {
      playerTimeChartData.push({
        day: i.range.from.value.toFormat('dd.MM'),
        playouts: i.data.productivePlayouts,
      });
    }
    chartData = {
      title: 'PlayoutPulse',
      height: 50,
      width: 100,
      valueAssignments: [
        {
          value: 'playouts',
          categories: ['day'],
          color: '#488FED', // sbBlue-500
          valueName: 'Playouts',
        },
      ],
      data: playerTimeChartData,
      loadingDone: true,
    };

    return chartData;
  }

  private retrieveCampaignReporting<T extends NamedIdentityDTO>(campaignIds: any[], campaigns: T[]) {
    return this.api
      .ds_cm_Reporting_campaignsReport({
        id: campaignIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(
        take(1),
        switchMap(data => {
          const buckets = data.buckets;
          const resultCampaigns = campaigns.map(campaign => {
            return { ...campaign, reporting: this.buildPulseChartData(buckets, campaign) };
          });
          return of(resultCampaigns);
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  private retrieveCommercialReporting<T extends NamedIdentityDTO>(commercialIds: any[], commercials: T[]) {
    return this.api
      .ds_cm_Reporting_commercialsReport({
        id: commercialIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(
        take(1),
        switchMap(data => {
          const buckets = data.buckets;
          const resultCampaigns = commercials.map(commercial => {
            return { ...commercial, reporting: this.buildPulseChartData(buckets, commercial) };
          });
          return of(resultCampaigns);
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  private retrieveRequirementsReporting<T extends NamedIdentityDTO>(requirementIds: any[], requirements: T[]) {
    return this.api
      .ds_cm_Reporting_requirementsReport({
        id: requirementIds,
        from: this.from,
        till: this.till,
        interval: 86400,
      })
      .pipe(
        take(1),
        switchMap(data => {
          const buckets = data.buckets;
          const resultCampaigns = requirements.map(requirement => {
            return { ...requirement, reporting: this.buildPulseChartData(buckets, requirement) };
          });
          return of(resultCampaigns);
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  private retrieveBookingsReporting<T extends NamedIdentityDTO>(bookingIds: any[], bookings: T[]) {
    return this.api.ds_cm_Reporting_bookingsReport({ id: bookingIds, interval: 86400 }).pipe(
      take(1),
      switchMap(data => {
        const buckets = data.buckets;
        const resultCampaigns = bookings.map(booking => {
          return { ...booking, reporting: this.buildPulseChartData(buckets, booking) };
        });
        return of(resultCampaigns);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }
}
